/**
 * Backend i18n payload shape — used by Product.name/description and other
 * multilingual fields. All three locales must be present in request bodies
 * for required fields (name); optional fields (description) may omit any.
 */
export interface LocalizedText {
  ru: string;
  en: string;
  uz: string;
}

export type LocalePartialText = Partial<LocalizedText>;

export type SupportedLocale = 'ru' | 'en' | 'uz';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['ru', 'en', 'uz'];

export function resolveLocale(input: string | undefined | null): SupportedLocale {
  if (!input) return 'en';
  if (input.startsWith('ru')) return 'ru';
  if (input.startsWith('uz')) return 'uz';
  return 'en';
}

/**
 * Pick a string from a localized payload with safe fallbacks: requested locale
 * → en → ru → uz → empty string. Accepts `null`/`undefined` so it's safe to
 * call directly on optional fields.
 */
export function pickLocalized(
  value: LocalePartialText | null | undefined,
  locale: string,
): string {
  if (!value) return '';
  const key = resolveLocale(locale);
  return (
    (value[key] ?? '').trim() ||
    (value.en ?? '').trim() ||
    (value.ru ?? '').trim() ||
    (value.uz ?? '').trim() ||
    ''
  );
}

/** True if every locale slot is blank (or value is null/undefined). */
export function isLocalizedBlank(value: LocalePartialText | null | undefined): boolean {
  if (!value) return true;
  return SUPPORTED_LOCALES.every((k) => !(value[k] ?? '').trim());
}
